import React, { FC, useContext, useEffect, useRef, useState } from "react";
import cn from "clsx";
import { isWindows } from "react-device-detect";
import isEmpty from "lodash/isEmpty";
import difference from "lodash/difference";
import groupBy from "lodash/groupBy";
import FilterOptionItem from "./filterOptionItem";
import { SelectedFiltersContext } from "../webFilteringSection";
import FilterElement from "./filterElement";
import { getDeepSelectedCategory } from "@/utils";
import { scrollParentToChild } from "@/util/dom";
import { CATEGORY_QUERY_PARAMS } from "../constants";
import { CategoryFilterOption, GeneralFacet, CategoryFacet } from "../types";
import {
  dropShallowRowsCoveredByDeeper,
  stripCategoryDeeperThanAnchor
} from "../utils";

type FilterCategoryProps = {
  filter: Omit<GeneralFacet & CategoryFacet, "sortOrder">;
};

type FilterCategoryOptionProps = {
  filterContent: CategoryFilterOption;
  onSelection: (...args) => void;
  listRef?: React.MutableRefObject<HTMLUListElement>;
};

const FilterCategoryWeb: FC<FilterCategoryProps> = ({ filter }) => {
  const { data: filterOptions } = filter;
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const { onSelection, updateSelectedFilters, selectedFilters } = useContext(
    SelectedFiltersContext
  );

  const listRef = useRef<HTMLUListElement>(null);

  const commitCategorySelection = (
    newFlatSelection: CategoryFilterOption[]
  ) => {
    const pruned = dropShallowRowsCoveredByDeeper(newFlatSelection);
    setSelectedOptions(pruned);

    const grouped = groupBy(pruned, "queryParam");

    onSelection(
      CATEGORY_QUERY_PARAMS.map(qp => ({
        type: filter.type,
        queryParam: qp,
        defaultLabel: filter.defaultLabel,
        selectedOptions: grouped[qp] || []
      }))
    );
  };

  const mergePreservedSameQueryParam = (
    queryParam: string,
    nextForParam: CategoryFilterOption[],
    sameLevelBeforeToggle: CategoryFilterOption[],
    clickedOption: CategoryFilterOption
  ): CategoryFilterOption[] => {
    const contextOpts = selectedFilters[queryParam]?.selectedOptions;
    if (!Array.isArray(contextOpts)) return nextForParam;

    const knownKeys = new Set(sameLevelBeforeToggle.map(o => String(o.key)));
    knownKeys.add(String(clickedOption.key));

    const preserved = contextOpts.filter(
      o => o?.key != null && !knownKeys.has(String(o.key))
    );

    if (preserved.length === 0) return nextForParam;

    return [...nextForParam, ...(preserved as CategoryFilterOption[])];
  };

  const onFilterOptionClick = ({ option, isLeaf = false }, event) => {
    event?.stopPropagation();

    const permissionToClick =
      isLeaf ||
      !selectedOptions.length ||
      selectedOptions[0].key !== option.key;

    if (permissionToClick) {
      let options = [];

      let filteredOptions: CategoryFilterOption[] = [];
      let leafAdded = false;
      if (isLeaf) {
        filteredOptions = selectedOptions.filter(
          selectedOption => selectedOption.queryParam === option.queryParam
        );

        const allreadyAdded = filteredOptions.find(
          selectedOption => selectedOption.key === option.key
        );

        if (allreadyAdded) {
          options = filteredOptions.filter(item => item.key !== option.key);
        } else {
          options = [...filteredOptions, option];
          leafAdded = true;
        }

        options = mergePreservedSameQueryParam(
          option.queryParam,
          options,
          filteredOptions,
          option
        );
      }

      const selectedOptionsToRemove =
        isLeaf &&
        option.children &&
        option.isSelected &&
        //@ts-ignore
        getDeepSelectedCategory({ data: option.children });

      if (selectedOptionsToRemove) {
        const groupedSelectedOptionsToRemove = groupBy(
          selectedOptionsToRemove,
          "queryParam"
        );

        const latestSelectedFilters = Object.keys(
          groupedSelectedOptionsToRemove
        ).reduce(
          (acc, key) => {
            acc[key] = {
              ...selectedFilters[key],
              selectedOptions: Array.isArray(
                selectedFilters[key]?.selectedOptions
              )
                ? selectedFilters[key].selectedOptions.filter(
                    selectedOption =>
                      !groupedSelectedOptionsToRemove[key].find(
                        removedOption =>
                          removedOption.key === selectedOption.key
                      )
                  )
                : []
            };
            return acc;
          },
          { ...selectedFilters }
        );

        onSelection(
          [
            {
              type: filter.type,
              queryParam: option.queryParam,
              defaultLabel: filter.defaultLabel,
              selectedOptions: isLeaf ? options : [option]
            }
          ],
          latestSelectedFilters
        );

        setSelectedOptions(
          difference(selectedOptions, selectedOptionsToRemove)
        );

        return;
      }

      const newFlatSelection = isLeaf
        ? [
            ...selectedOptions.filter(
              (s: CategoryFilterOption) => s.queryParam !== option.queryParam
            ),
            ...options
          ]
        : [option];

      const flatToCommit =
        isLeaf && leafAdded
          ? stripCategoryDeeperThanAnchor(newFlatSelection, option)
          : newFlatSelection;

      commitCategorySelection(flatToCommit);
    }
  };

  useEffect(() => {
    const selectedOption = getDeepSelectedCategory(filter);

    if (!isEmpty(selectedOption)) {
      const updatedFilter = selectedOption.reduce((acc, item) => {
        if (isEmpty(acc)) {
          return {
            type: filter.type,
            label: item.label,
            defaultLabel: filter.defaultLabel,
            queryParam: item.queryParam,
            selectedOptions: [item]
          };
        } else {
          acc.selectedOptions.push(item);
        }
        return acc;
      }, {} as any);

      updateSelectedFilters(updatedFilter);
    }
  }, []);

  useEffect(() => {
    const selectedOptionByResponse = getDeepSelectedCategory(filter);
    setSelectedOptions(selectedOptionByResponse);
  }, [filter]);

  return (
    <FilterElement
      className={cn({
        items_selected: !!selectedOptions.length
      })}
      filterLabel={filter.label}
      filterSelectedCount={selectedOptions.length || undefined}
      filterWrapperClassName="categories mxw-300"
    >
      <div>
        <ul
          ref={listRef}
          className={cn("filter_category_scrollbar", {
            windows_scrollbar: isWindows
          })}
        >
          {filterOptions.map(option => (
            <RootFilterContent
              listRef={listRef}
              key={option.key}
              filterContent={option}
              onSelection={onFilterOptionClick}
            />
          ))}
        </ul>
      </div>
    </FilterElement>
  );
};

const LeafComponent: FC<FilterCategoryOptionProps> = ({
  filterContent,
  onSelection,
  listRef
}) => {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (filterContent.isSelected) {
      scrollParentToChild(listRef.current, ref.current);
    }
  }, []);

  return (
    <FilterOptionItem
      key={filterContent.key}
      option={filterContent}
      isSelected={filterContent.isSelected}
      onSelection={onSelection}
      containerRef={ref}
    />
  );
};

const ExpandableComponent: FC<FilterCategoryOptionProps> = ({
  filterContent,
  onSelection,
  listRef
}) => {
  const [isOpen, setIsOpen] = useState(filterContent.isSelected);
  const [selected, setSelected] = useState(false);

  const toggleOpen = () => {
    setIsOpen(isOpen => !isOpen);
  };

  const onExtraLabelClick = ({ isLeaf }, event) => {
    onSelection(
      {
        option: {
          ...filterContent,
          selectedOptions: [filterContent]
        },
        isLeaf
      },
      event
    );
  };

  useEffect(() => {
    const selectionState =
      filterContent.isSelected &&
      filterContent.children.every(item => !item.isSelected);
    setSelected(selectionState);
  }, [filterContent]);

  return (
    <li>
      <h5
        className={cn("cursor_pointer", "category_name", "d-flex", {
          selected: filterContent.isSelected,
          expanded: isOpen
        })}
        onClick={toggleOpen}
      >
        <div className="expendable_icon" />
        {filterContent.label}
      </h5>
      <ul className={cn({ "d-none": !isOpen })}>
        {filterContent?.extraLabel && (
          <FilterOptionItem
            option={{
              key: filterContent.key,
              label: filterContent.extraLabel,
              defaultValue: filterContent.defaultValue,
              queryParam: filterContent.queryParam,
              isSelected: filterContent.isSelected
            }}
            isSelected={selected}
            onSelection={onExtraLabelClick}
          />
        )}
        {filterContent.children.map(option => (
          <RootFilterContent
            listRef={listRef}
            key={option.key}
            filterContent={option}
            onSelection={onSelection}
          />
        ))}
      </ul>
    </li>
  );
};

const RootFilterContent: FC<FilterCategoryOptionProps> = ({
  filterContent,
  onSelection,
  listRef
}) => {
  const isLeaf = isEmpty(filterContent.children);

  if (isLeaf) {
    return (
      <LeafComponent
        filterContent={filterContent}
        onSelection={onSelection}
        listRef={listRef}
      />
    );
  }

  return (
    <ExpandableComponent
      filterContent={filterContent}
      onSelection={onSelection}
      listRef={listRef}
    />
  );
};

export default FilterCategoryWeb;
